import { HOST_AI_FIELD_KEYS } from '../constants';
import { resolveListingAvailability } from '../../../utils/availabilitySchedule';
import { insuranceToPayload, normalizeDrawerInsurance, normalizeInsurance } from '../../../utils/insuranceHelpers';
import { flattenInstructions, isStructuredInstructions } from '../../../utils/parkingInstructions';

export { HOST_AI_FIELD_KEYS };

export function isBulkDraftListing(listing) {
    return listing?.source === 'bulk' && listing?.status === 'draft';
}

// PP-342 Phase 1 — a live, host-editable listing. Paused / archived
// listings are intentionally excluded so the host can't change pricing
// or availability mid-pause; they can resume → edit.
export function isLiveEditableListing(listing) {
    return listing?._v5Status === 'live' && !listing?.isPaused && !listing?.isArchived;
}

// Whitelist of confirmedFields inner keys editable on a live listing.
// Matches the server-side LIVE_EDITABLE_CONFIRMED_FIELDS_KEYS gate in
// server/routes/listings.js. `availability` is intentionally absent —
// it ships in Phase 2 with the review workflow + per-booking conflict
// detection. This set ONLY narrows the live-mode payload filter in
// buildSavePayload; bulk-draft mode is unaffected.
const LIVE_CONFIRMED_FIELDS_KEYS = new Set(['instructions', 'amenities']);

function getConfirmedValue(listing, key) {
    const cf = listing.confirmedFields || {};
    if (cf[key] != null && cf[key] !== '') return cf[key];
    const upload = listing.uploadFields?.[key];
    if (upload != null && String(upload).trim() !== '') return upload;
    const ef = listing.enrichmentFields?.[key];
    if (ef == null) return '';
    return ef?.value ?? ef ?? '';
}

function resolveGeoSpaceCount(listing) {
    const gsc = listing.geoSpaceCount;
    if (gsc == null) return null;
    if (typeof gsc === 'number') return gsc;
    return gsc.suggestedCount ?? null;
}

// F-409: an AI field as the text the host sees and edits. Directions may be
// saved as structured approaches (PP-492); String() on those printed
// "[object Object]". flattenInstructions also turns the PP-488 "coming soon"
// sentence into no text (#405). Every comparison below uses the same text,
// so an untouched field is never re-saved as a different value.
function fieldText(key, value) {
    return key === 'instructions' ? flattenInstructions(value) : String(value ?? '');
}

export function normalize(value) {
    return String(value ?? '').trim().toLowerCase().replace(/\s+/g, ' ');
}

export function hasUploadProvenance(listing, key) {
    const uploadVal = listing?.uploadFields?.[key];
    return uploadVal != null && String(uploadVal).trim() !== '';
}

export function isUserProvidedValue(listing, key, currentVal) {
    const normalizedCurrent = normalize(currentVal);
    if (!normalizedCurrent) return false;

    const uploadVal = listing.uploadFields?.[key];
    if (uploadVal != null && normalize(uploadVal) === normalizedCurrent) return true;

    const enrichField = listing.enrichmentFields?.[key];
    if (enrichField == null) return true;

    return false;
}

function buildInitialAiFieldStates(listing, aiFields) {
    const enrichment = listing.enrichmentFields || {};
    const confirmedFields = listing.confirmedFields || {};
    const states = {};

    for (const key of Object.keys(aiFields)) {
        const currentVal = String(aiFields[key] ?? '');
        const enrichField = enrichment[key];
        const aiBaseline = fieldText(key, enrichField?.value ?? enrichField);
        const uploadVal = listing.uploadFields?.[key];

        const hasExplicitConfirmation =
            confirmedFields[key] != null && String(confirmedFields[key]) !== '';

        if (uploadVal != null && normalize(uploadVal) === normalize(currentVal) && currentVal !== '') {
            states[key] = 'upload';
            continue;
        }

        if (currentVal !== '' && enrichField != null && normalize(currentVal) !== normalize(aiBaseline)) {
            states[key] = 'edited';
            continue;
        }

        if (hasExplicitConfirmation || enrichField?.confidence === 'high') {
            states[key] = 'confirmed';
            continue;
        }

        states[key] = 'pending';
    }
    return states;
}

export function buildInitialDirtyFields(listing) {
    const aiFields = {};
    for (const key of HOST_AI_FIELD_KEYS) {
        const val = getConfirmedValue(listing, key);
        if (val === '' && !listing.uploadFields?.[key] && !listing.enrichmentFields?.[key]) continue;
        aiFields[key] = fieldText(key, val);
    }

    const amenities = listing.confirmedFields?.amenities;

    const geoCount = resolveGeoSpaceCount(listing);

    return {
        aiFields,
        aiFieldStates: buildInitialAiFieldStates(listing, aiFields),
        amenities: Array.isArray(amenities) ? [...amenities] : [],
        amenitiesDismissed: [...(listing.enrichmentFields?.amenitiesDismissed ?? [])],
        pricing: {
            hourlyRate: listing.pricing?.hourlyRate ?? null,
        },
        maxSpots: listing.pricing?.maxSpots ?? geoCount ?? 250,
        maxSpotsLocked: listing.pricing?.maxSpotsLocked ?? false,
        availability: resolveListingAvailability(listing),
        insurance: normalizeDrawerInsurance(listing),
        // PP-419 — photos are staged on the drawer; the modal's Save writes
        // into this array via PropertyDrawer.stagePhotos, and Save Changes
        // includes them in the PUT so the update is atomic with the rest of
        // the edit. hasDirtyChanges catches photo mutations via the JSON
        // stringify comparison.
        photos: Array.isArray(listing.photos) ? [...listing.photos] : [],
    };
}

export function hasDirtyChanges(baseline, current) {
    if (!baseline || !current) return false;
    return JSON.stringify(baseline) !== JSON.stringify(current);
}

export function buildSavePayload(listing, dirtyFields, options = {}) {
    const { mode = 'bulk-draft' } = options;
    const body = {};
    const confirmedFields = { ...(listing.confirmedFields || {}) };
    let confirmedChanged = false;

    const aiFieldStates = dirtyFields.aiFieldStates || {};
    for (const [key, value] of Object.entries(dirtyFields.aiFields || {})) {
        const state = aiFieldStates[key];
        if (state !== 'confirmed' && state !== 'edited' && state !== 'upload') continue;

        const prev = fieldText(key, confirmedFields[key]);
        const next = String(value).trim();
        if (!next) continue;
        if (next !== prev) {
            // F-409: an AI suggestion saved as it stands keeps its per-road
            // structure; only text the host actually wrote is saved as text.
            const aiSuggestion = key === 'instructions' ? listing.enrichmentFields?.instructions : null;
            const savesSuggestionAsIs = isStructuredInstructions(aiSuggestion)
                && next === flattenInstructions(aiSuggestion).trim();
            confirmedFields[key] = savesSuggestionAsIs ? aiSuggestion : next;
            confirmedChanged = true;
        }
    }

    const prevAmenities = listing.confirmedFields?.amenities ?? [];
    if (JSON.stringify(dirtyFields.amenities ?? []) !== JSON.stringify(prevAmenities)) {
        confirmedFields.amenities = dirtyFields.amenities ?? [];
        confirmedChanged = true;
    }

    const prevAvailability = listing.confirmedFields?.availability ?? null;
    if (JSON.stringify(dirtyFields.availability ?? null) !== JSON.stringify(prevAvailability)) {
        confirmedFields.availability = dirtyFields.availability ?? null;
        confirmedChanged = true;
    }

    const prevDismissed = listing.enrichmentFields?.amenitiesDismissed ?? [];
    if (JSON.stringify(dirtyFields.amenitiesDismissed ?? []) !== JSON.stringify(prevDismissed)) {
        body.enrichmentFields = {
            ...(listing.enrichmentFields || {}),
            ...(body.enrichmentFields || {}),
            amenitiesDismissed: dirtyFields.amenitiesDismissed ?? [],
        };
    }

    if (confirmedChanged) {
        body.confirmedFields = confirmedFields;
    }

    const pricing = { ...(listing.pricing || {}) };
    let pricingChanged = false;
    const maxSpots = Number(dirtyFields.maxSpots);

    if (maxSpots !== (listing.pricing?.maxSpots ?? null)) {
        pricing.maxSpots = maxSpots;
        pricingChanged = true;
    }
    if (dirtyFields.maxSpotsLocked !== (listing.pricing?.maxSpotsLocked ?? false)) {
        pricing.maxSpotsLocked = dirtyFields.maxSpotsLocked;
        pricingChanged = true;
    }
    const dirtyRate = dirtyFields.pricing?.hourlyRate;
    if (dirtyRate !== undefined && dirtyRate !== (listing.pricing?.hourlyRate ?? null)) {
        pricing.hourlyRate = dirtyRate;
        pricingChanged = true;
    }
    if (pricingChanged) {
        body.pricing = pricing;
    }

    const prevInsurance = normalizeDrawerInsurance(listing);
    const nextInsurance = dirtyFields.insurance != null
        ? normalizeInsurance(dirtyFields.insurance)
        : prevInsurance;
    if (JSON.stringify(insuranceToPayload(nextInsurance)) !== JSON.stringify(insuranceToPayload(prevInsurance))) {
        body.insurance = insuranceToPayload(nextInsurance);
    }

    // PP-419 — photos ride the same atomic Save Changes PUT as pricing /
    // spots / instructions, so the drawer commits a single coherent update
    // instead of the photos modal PUTting on its own.
    if (Array.isArray(dirtyFields.photos)) {
        const prevPhotos = Array.isArray(listing.photos) ? listing.photos : [];
        if (JSON.stringify(dirtyFields.photos) !== JSON.stringify(prevPhotos)) {
            body.photos = dirtyFields.photos;
        }
    }

    // PP-342 Phase 1 — strip fields the server's live-status gate would
    // 422 on. Insurance + enrichmentFields are bulk/wizard-only flows;
    // confirmedFields is restricted to the live allowlist.
    if (mode === 'live') {
        delete body.insurance;
        delete body.enrichmentFields;
        if (body.confirmedFields) {
            const filtered = {};
            for (const key of Object.keys(body.confirmedFields)) {
                if (LIVE_CONFIRMED_FIELDS_KEYS.has(key)) {
                    filtered[key] = body.confirmedFields[key];
                }
            }
            if (Object.keys(filtered).length > 0) {
                body.confirmedFields = filtered;
            } else {
                delete body.confirmedFields;
            }
        }
    }

    return body;
}

// PP-419 — a live listing must always carry at least one photo. PP-417
// gated the photos-modal save + any PUT that carries `photos: []`. The
// drawer's Save Changes previously sent only dirty non-photo fields; the
// PP-419 follow-up moves photos into dirtyFields, so this guard should
// evaluate the RESULTING photo count (staged photos win over persisted).
// Pass the merged/display listing (mergeDirtyFieldsIntoListing) or a
// dirtyFields.photos-aware view — plain persisted listing.photos would
// miss photos the user just added via the modal.
// Bulk drafts are exempt; the wizard's submit-for-review gate covers them.
export function getLivePhotoBlockingError(listing, isLiveEditable) {
    if (!isLiveEditable) return null;
    if ((listing?.photos?.length ?? 0) > 0) return null;
    return 'Add at least one photo before saving changes.';
}

export function validateDirtyFieldsClient(dirtyFields, listing) {
    const errors = {};
    const n = Number(dirtyFields.maxSpots);
    if (!Number.isInteger(n) || n < 10 || n > 2000) {
        errors.maxSpots = 'Must be between 10 and 2000';
    }
    const rate = dirtyFields.pricing?.hourlyRate ?? listing?.pricing?.hourlyRate;
    if (rate == null || Number(rate) <= 0) {
        errors.hourlyRate = 'Hourly rate is required';
    }
    return errors;
}

export function isFieldDirty(baseline, dirty, key) {
    if (!baseline || !dirty) return false;
    return baseline[key] !== dirty[key];
}

export function isAiFieldDirty(baseline, dirty, key) {
    if (!baseline?.aiFields || !dirty?.aiFields) return false;
    return baseline.aiFields[key] !== dirty.aiFields[key];
}

export function isAmenitiesDirty(baseline, dirty) {
    if (!baseline || !dirty) return false;
    return JSON.stringify(baseline.amenities ?? []) !== JSON.stringify(dirty.amenities ?? []);
}

export function mergeDirtyFieldsIntoListing(listing, dirtyFields) {
    if (!listing || !dirtyFields) return listing;

    const confirmedFields = { ...(listing.confirmedFields || {}) };

    const aiFields = dirtyFields.aiFields || {};
    const aiFieldStates = dirtyFields.aiFieldStates || {};
    for (const [key, value] of Object.entries(aiFields)) {
        const state = aiFieldStates[key];
        if (state && state !== 'pending' && value != null && String(value).trim() !== '') {
            confirmedFields[key] = value;
        }
    }

    if (Array.isArray(dirtyFields.amenities)) {
        confirmedFields.amenities = dirtyFields.amenities;
    }

    if (dirtyFields.availability != null) {
        confirmedFields.availability = dirtyFields.availability;
    }

    const insurance = dirtyFields.insurance
        ? insuranceToPayload(dirtyFields.insurance)
        : listing.insurance;

    const pricing = { ...(listing.pricing || {}) };
    if (dirtyFields.maxSpots != null) pricing.maxSpots = dirtyFields.maxSpots;
    if (dirtyFields.maxSpotsLocked != null) pricing.maxSpotsLocked = dirtyFields.maxSpotsLocked;
    if (dirtyFields.pricing?.hourlyRate !== undefined) {
        pricing.hourlyRate = dirtyFields.pricing.hourlyRate;
    }

    const enrichmentFields = { ...(listing.enrichmentFields || {}) };
    if (Array.isArray(dirtyFields.amenitiesDismissed)) {
        enrichmentFields.amenitiesDismissed = dirtyFields.amenitiesDismissed;
    }

    // PP-419 — reflect staged photos in the merged view so PhotosPreview
    // renders the pending set, not the persisted one, while the drawer is
    // in edit mode.
    const photos = Array.isArray(dirtyFields.photos)
        ? dirtyFields.photos
        : listing.photos;

    return { ...listing, confirmedFields, pricing, enrichmentFields, insurance, photos };
}
